import { TriangleAlertIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useDrift } from '@/lib/drift';

/** Distinct fields shown before the tooltip stops listing them individually. */
const MAX_LISTED = 4;

/**
 * That some rows could not be read, and which ones.
 *
 * The parse layer drops a row it cannot understand instead of failing the whole
 * screen, which is the right trade only while the drop is visible. A list
 * quietly one row short looks exactly like a list, gets read as data, and the
 * disagreement between this build and the servers is discovered later and
 * somewhere more expensive.
 *
 * Absent when nothing has been dropped, which is almost always, so it costs a
 * reader nothing on a healthy deploy. It is not dismissable: it disappears when
 * the condition does, and a badge you can dismiss is a badge nobody sees twice.
 */
export function DriftBadge() {
  const drift = useDrift();
  if (drift.length === 0) return null;

  const fields = [...new Set(drift.map((record) => record.detail))];
  const listed = fields.slice(0, MAX_LISTED);
  const remaining = fields.length - listed.length;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className="gap-1.5 border-amber-500/40 font-normal text-amber-500"
        >
          <TriangleAlertIcon className="size-3" aria-hidden />
          {drift.length} unreadable
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm">
        <p className="font-medium">
          {drift.length === 1
            ? 'One row could not be read and was left out.'
            : `${String(drift.length)} rows could not be read and were left out.`}
        </p>
        <p className="mt-1">
          This build and the API disagree about a field. Everything else on the
          screen is accurate; only the rows listed below are missing.
        </p>
        <ul className="mt-1.5 space-y-0.5 font-mono text-xs">
          {listed.map((field) => (
            <li key={field}>{field}</li>
          ))}
          {remaining > 0 ? <li>and {remaining} more</li> : null}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
}
